import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../home-landing/components/Footer';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { projectService } from '../../services/projectService';

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState(['All']);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projects, selectedCategory, searchQuery]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService?.getActiveProjects();
      setProjects(data || []);
      
      // Extract unique categories
      const uniqueCategories = ['All', ...new Set(data?.map(p => p?.category)?.filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered?.filter(p => p?.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase()?.trim();
      filtered = filtered?.filter(p => 
        p?.title?.toLowerCase()?.includes(query) ||
        p?.description?.toLowerCase()?.includes(query) ||
        p?.technologies?.some(tech => tech?.toLowerCase()?.includes(query))
      );
    }

    setFilteredProjects(filtered);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e?.target?.value || '');
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Projects - Joe Graziano</title>
        </Helmet>
        <Header />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-accent mx-auto mb-4"></div>
            <p className="text-muted-foreground text-lg">Loading projects...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Projects - Joe Graziano | Portfolio Showcase</title>
        <meta name="description" content="Explore my portfolio of projects including full-stack development, enterprise applications, and SaaS products with cutting-edge technologies." />
      </Helmet>
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16 md:py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-2 bg-accent/20 text-accent rounded-full text-sm md:text-base font-medium mb-4">
                Projects
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                My Project Portfolio
              </h1>
              <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto">
                Showcasing impactful solutions that drive business results and deliver exceptional user experiences
              </p>
            </div>
          </div>
        </section>

        {/* Filters and Search */}
        <section className="py-8 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6">
              {/* Search Bar */}
              <div className="flex-1">
                <div className="relative">
                  <Icon 
                    name="Search" 
                    size={20} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                  />
                  <input
                    type="text"
                    placeholder="Search projects by title, description, or technology..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
                {categories?.map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                      selectedCategory === category
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted border border-border'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-semibold text-foreground">{filteredProjects?.length}</span> of <span className="font-semibold text-foreground">{projects?.length}</span> projects
              </p>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            {filteredProjects?.length === 0 ? (
              <div className="text-center py-16">
                <Icon name="FolderOpen" size={64} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No projects found</h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery || selectedCategory !== 'All' ?'Try adjusting your filters or search query' :'No projects available at the moment'}
                </p>
                {(searchQuery || selectedCategory !== 'All') && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('All');
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filteredProjects?.map((project) => (
                  <div
                    key={project?.id}
                    className="group bg-card rounded-xl md:rounded-2xl overflow-hidden border border-border hover:border-accent/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                  >
                    {/* Project Image */}
                    <div className="relative h-48 md:h-56 overflow-hidden">
                      <Image
                        src={project?.image}
                        alt={project?.imageAlt}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Category Badge */}
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-accent text-accent-foreground text-xs md:text-sm rounded-full font-medium">
                          {project?.category}
                        </span>
                      </div>

                      {/* Status Badge */}
                      {project?.status && (
                        <div className="absolute top-4 left-4">
                          <span className={`px-3 py-1 text-xs md:text-sm rounded-full font-medium ${
                            project?.status === 'active' ?'bg-green-500/20 text-green-400 border border-green-500/30' :'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          }`}>
                            {project?.status === 'active' ? 'Active' : 'Completed'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Project Content */}
                    <div className="p-6 md:p-8">
                      <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2 group-hover:text-accent transition-colors duration-300">
                        {project?.title}
                      </h3>

                      <p className="text-sm md:text-base text-muted-foreground mb-4 line-clamp-3">
                        {project?.description}
                      </p>

                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project?.technologies?.slice(0, 4)?.map((tech, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md border border-border"
                          >
                            {tech}
                          </span>
                        ))}
                        {project?.technologies?.length > 4 && (
                          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md border border-border">
                            +{project?.technologies?.length - 4} more
                          </span>
                        )}
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-3 gap-4 mb-4 pt-4 border-t border-border">
                        <div className="text-center">
                          <div className="text-base md:text-lg font-semibold text-foreground">
                            {project?.metricsUsers}
                          </div>
                          <div className="text-xs text-muted-foreground">Users</div>
                        </div>
                        <div className="text-center">
                          <div className="text-base md:text-lg font-semibold text-foreground">
                            {project?.metricsPerformance}
                          </div>
                          <div className="text-xs text-muted-foreground">Uptime</div>
                        </div>
                        <div className="text-center">
                          <div className="text-base md:text-lg font-semibold text-foreground">
                            {project?.metricsRating}
                          </div>
                          <div className="text-xs text-muted-foreground">Rating</div>
                        </div>
                      </div>

                      {/* View Details Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ArrowRight"
                        iconPosition="right"
                        fullWidth
                        className="group-hover:bg-accent group-hover:text-accent-foreground group-hover:border-accent"
                        onClick={() => {
                          if (project?.link) {
                            window.open(project?.link, '_blank', 'noopener,noreferrer');
                          } else {
                            alert('Project details coming soon!');
                          }
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Projects;